export const VIMEO_STARTUP_QUALITY = "720p";
const QUALITY_LADDER = ["720p", "540p", "360p", "240p"];
const STARTUP_STALL_MS = 6000;
const RECOVERY_PAUSE_MS = 8000;
const MAX_RECOVERY_ATTEMPTS = 2;
const RESTORE_AUTO_AFTER_MS = 5000;

function getConnection() {
    if (typeof navigator === "undefined") return null;
    return navigator.connection || navigator.mozConnection || navigator.webkitConnection || null;
}

function isMobileClient() {
    if (typeof navigator === "undefined") return false;
    if (typeof window !== "undefined" && window.matchMedia && window.matchMedia("(max-width: 900px)").matches) {
        return true;
    }
    return /iPhone|iPad|iPod|Android|Mobile/i.test(navigator.userAgent || "");
}

export function shouldCapVimeoStartupQuality() {
    const connection = getConnection();
    if (connection) {
        if (connection.saveData) return true;
        if (connection.type === "cellular") return true;
        if (["slow-2g", "2g", "3g"].includes(connection.effectiveType)) return true;
    }
    // iOS Safari does not expose Network Information API, so cap phones/tablets too.
    return isMobileClient();
}

export function getVimeoStartupQuality() {
    return VIMEO_STARTUP_QUALITY;
}

export function withVimeoStartupParams(url) {
    if (!url || typeof url !== "string" || !/vimeo\.com/i.test(url)) return url;
    // Iframe embeds cannot restore Auto later, so only lock quality on constrained networks.
    if (!shouldCapVimeoStartupQuality()) return url;
    try {
        const parsed = new URL(url, "https://player.vimeo.com");
        if (!parsed.searchParams.has("quality")) {
            parsed.searchParams.set("quality", VIMEO_STARTUP_QUALITY);
        }
        return parsed.toString();
    } catch {
        return url;
    }
}

function qualityRank(id) {
    const index = QUALITY_LADDER.indexOf(id);
    return index === -1 ? Number.POSITIVE_INFINITY : index;
}

async function pickAvailableQuality(player, preferredQuality) {
    const target = preferredQuality || VIMEO_STARTUP_QUALITY;
    try {
        const qualities = await player.getQualities();
        const ids = (qualities || [])
            .map((item) => (typeof item === "string" ? item : item && item.id))
            .filter(Boolean)
            .filter((id) => id !== "auto");
        if (!ids.length) return target;
        if (ids.includes(target)) return target;
        const ranked = QUALITY_LADDER.filter((id) => ids.includes(id));
        if (ranked.length) {
            const targetRank = qualityRank(target);
            return ranked.find((id) => qualityRank(id) >= targetRank) || ranked[ranked.length - 1];
        }
        return ids[ids.length - 1];
    } catch {
        return target;
    }
}

async function setPlayerQuality(player, preferredQuality) {
    const quality = await pickAvailableQuality(player, preferredQuality);
    try {
        await player.setQuality(quality);
        return quality;
    } catch {
        return null;
    }
}

export function attachVimeoPlaybackGuards(player, options = {}) {
    const preferredQuality = options.preferredQuality || getVimeoStartupQuality();
    const restoreAuto = options.restoreAuto ?? !shouldCapVimeoStartupQuality();

    let hasStartedPlaying = false;
    let recovering = false;
    let recoveryAttempts = 0;
    let stallTimer = null;
    let restoreTimer = null;
    let currentQuality = preferredQuality || VIMEO_STARTUP_QUALITY;

    const clearStall = () => {
        if (stallTimer) {
            clearTimeout(stallTimer);
            stallTimer = null;
        }
    };
    const clearRestore = () => {
        if (restoreTimer) {
            clearTimeout(restoreTimer);
            restoreTimer = null;
        }
    };
    const armStallTimer = () => {
        clearStall();
        stallTimer = setTimeout(recoverFromStartupStall, STARTUP_STALL_MS);
    };

    const capQuality = (quality = currentQuality) => setPlayerQuality(player, quality);

    const markPlaying = () => {
        hasStartedPlaying = true;
        recovering = false;
        clearStall();
        if (restoreAuto && !restoreTimer) {
            restoreTimer = setTimeout(() => {
                player.setQuality("auto").catch(() => {});
            }, RESTORE_AUTO_AFTER_MS);
        }
    };

    const recoverFromStartupStall = async () => {
        if (hasStartedPlaying || recovering || recoveryAttempts >= MAX_RECOVERY_ATTEMPTS) return;
        recovering = true;
        recoveryAttempts += 1;
        const nextQuality = QUALITY_LADDER[Math.min(recoveryAttempts, QUALITY_LADDER.length - 1)];
        currentQuality = nextQuality;
        try {
            await capQuality(nextQuality);
            await player.pause();
            await new Promise((resolve) => setTimeout(resolve, RECOVERY_PAUSE_MS));
            if (!hasStartedPlaying) {
                await player.play();
            }
        } catch {
            // Ignore player errors during recovery; a later play gesture can retry.
        } finally {
            recovering = false;
            if (!hasStartedPlaying) armStallTimer();
        }
    };

    const onLoaded = () => {
        hasStartedPlaying = false;
        recovering = false;
        recoveryAttempts = 0;
        currentQuality = preferredQuality || VIMEO_STARTUP_QUALITY;
        clearStall();
        clearRestore();
        if (preferredQuality) {
            capQuality(preferredQuality);
        }
    };

    const onPlay = () => {
        if (hasStartedPlaying || recovering) return;
        armStallTimer();
    };

    const onTimeUpdate = (event) => {
        if (event && event.seconds > 0.3) markPlaying();
    };

    const onPause = () => {
        if (!recovering) clearStall();
    };

    player.ready().then(() => {
        if (preferredQuality) capQuality(preferredQuality);
    }).catch(() => {});

    player.on("loaded", onLoaded);
    player.on("play", onPlay);
    player.on("playing", markPlaying);
    player.on("timeupdate", onTimeUpdate);
    player.on("pause", onPause);

    return function detach() {
        clearStall();
        clearRestore();
        try {
            player.off("loaded", onLoaded);
            player.off("play", onPlay);
            player.off("playing", markPlaying);
            player.off("timeupdate", onTimeUpdate);
            player.off("pause", onPause);
        } catch {
            // Player may already be destroyed.
        }
    };
}
